import queryString from 'query-string';
import Pikaday from 'pikaday';
import BasePage from '../../components/base-page/base-page';

import analytics, { events } from '../../../shared/analytics';

import '../../components/_datepicker.scss';
import './gift-cards.scss';
import config from '../../../shared/config/config';
import jump from '../../utils/jump';


class GiftCardsPage extends BasePage {
  ui = {
    form: '.js-gift-card-form',
    formDatepicker: '.js-form-datepicker',
    formTotalProductName: '.js-form-total-product-name',
    formTotalPrice: '.js-form-total-price',
    formSubmitButton: '.js-form-submit-btn',
  };

  uiCollections = {
    cardsSelectorInput: '.js-gift-card-selector-input',
    scheduleOptionRadios: '.js-form-schedule-option',
  };

  events = {
    'change @uiCollections.cardsSelectorInput': 'handleGiftCardChange',
    'change @uiCollections.scheduleOptionRadios': 'handleScheduleRadioChange',
    'submit @ui.form': 'handleFormSubmit',
  };

  isSandbox = config.get('paddleUseSandbox');

  onInit() {
    super.onInit();

    const selectedCard = this.getSelectedGiftCard();

    this.initPaddle();
    this.datePicker = new Pikaday({
      field: this.ui.formDatepicker,
      theme: 'datepicker',
      minDate: new Date(),
      parse(dateString) {
        const parts = dateString.split('-');
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);

        return new Date(year, month, day);
      },
      toString(date) {
        const day = date.getDate();
        const month = date.getMonth() + 1;
        const year = date.getFullYear();

        return `${year}-${month}-${day}`;
      },
    });

    this.setDatepickerTodayDate();
    this.updateTotalPrice(selectedCard.dataset.price);
    this.updateTotalProductName(selectedCard.dataset.name);
  }

  initPaddle = () => {
    window.Paddle.Setup({
      vendor: config.get('paddleVendorId'),
    });

    if (this.isSandbox) {
      window.Paddle.Environment.set('sandbox');
    }
  };

  setDatepickerTodayDate = () => {
    const today = new Date().toISOString().substr(0, 10);

    this.datePicker.setDate(today);
  };

  handleScheduleRadioChange = (e) => {
    this.setDatepickerTodayDate();
    this.ui.formDatepicker.disabled = !e.target.dataset.datepickerActive;
  };

  handleGiftCardChange = (e) => {
    this.updateTotalPrice(e.target.dataset.price);
    this.updateTotalProductName(e.target.dataset.name);
    this.jumpToForm();
  };

  getFormData = () => {
    const { form } = this.ui;

    return {
      recipient_name: form.elements.recipient_name.value,
      recipient_email: form.elements.recipient_email.value,
      sender_name: form.elements.sender_name.value,
      sender_email: form.elements.sender_email.value,
      note: form.elements.note.value,
      date: form.elements.schedule.value,
    };
  };

  handleFormSubmit = (e) => {
    e.preventDefault();

    if (!this.ui.form.checkValidity()) {
      return;
    }

    const selectedGiftCard = this.getSelectedGiftCard();
    const productId = selectedGiftCard.dataset.paddleProductId;
    const coupon = selectedGiftCard.dataset.paddleCoupon;
    const formData = this.getFormData();

    this.sendAnalyticsOnProceedToPaymentButtonClick();

    this.disableSubmitButton();
    this.openCheckout({ productId, coupon, formData });
  };

  disableSubmitButton = () => {
    this.ui.formSubmitButton.disabled = true;
  };

  enableSubmitButton = () => {
    this.ui.formSubmitButton.disabled = false;
  };

  handleCheckoutSuccess = (checkoutData) => {
    const checkoutId = checkoutData.checkout.id;

    this.sendAnalyticsOnPayNowPaddleButtonClick();

    window.Paddle.Order.details(checkoutId, (orderData) => {
      if (orderData) {
        const isAffiliatePurchase = 'isAffiliateVisit' in this.ui.form.dataset;
        const successPurchasePageLink = this.buildSuccessPurchasePageLink(orderData);

        if (isAffiliatePurchase) {
          this.sendAnalyticsAffiliatePurchase(checkoutData, orderData);
        }

        window.location.assign(successPurchasePageLink);
      } else {
        this.enableSubmitButton();
      }
    });
  };

  handleCheckoutClose = () => {
    this.enableSubmitButton();
  };

  buildSuccessPurchasePageLink = (orderData) => {
    const {
      checkout: { image_url: cardIcon },
      lockers: [{ license_code: cardCode, product_name: cardType }],
    } = orderData;
    const receiveDate = this.getFormData().date;

    const queryParams = {
      cardCode,
      cardType,
      cardIcon,
      receiveDate,
    };

    return `/gift-cards/successful-purchase?${queryString.stringify(queryParams)}`;
  };

  updateTotalPrice = (price) => {
    this.ui.formTotalPrice.textContent = price;
  };

  updateTotalProductName = (name) => {
    this.ui.formTotalProductName.textContent = name;
  };

  jumpToForm() {
    jump(this.ui.form, {
      offset: -200, // sticky navigation height margin + form title
    });
  }

  getSelectedGiftCard = () => this.uiCollections.cardsSelectorInput.filter((el) => el.checked)[0];

  openCheckout({ productId, coupon, formData }) {
    window.Paddle.Checkout.open({
      product: productId,
      email: formData.sender_email,
      coupon,
      successCallback: this.handleCheckoutSuccess,
      closeCallback: this.handleCheckoutClose,
      allowQuantity: false,
      passthrough: JSON.stringify(formData),
    });
  }

  sendAnalyticsAffiliatePurchase = (checkoutData, orderData) => {
    const { order_id: orderId } = orderData.order;
    const [{ product_name: productName }] = orderData.lockers;
    const { quantity } = checkoutData.product;
    const { coupon, prices } = checkoutData.checkout;
    const { currency, total, total_tax: totalTax } = prices.vendor;

    window.dataLayer.push({
      event: 'affiliate gift card purchase',
      orderId,
      currencyCode: currency,
      orderPromoCode: coupon,
      items: [
        {
          subTotal: Number((total - totalTax).toFixed(2)),
          category: 'Gift Cards',
          sku: productName,
          quantity,
        },
      ],
    });
  };

  sendAnalyticsOnPayNowPaddleButtonClick = () => {
    analytics.trackEvent(events.GIFT_CARD_PAY_NOW_CLICK);
  };

  sendAnalyticsOnProceedToPaymentButtonClick = () => {
    analytics.trackEvent(events.GIFT_CARD_PROCEED_TO_PAYMENT);
  };
}

export default GiftCardsPage;

GiftCardsPage.initOnDomLoaded();
